// stores/businessTypeStore.js
'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
  try {
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateBusinessTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/business-type/search');
};

export const BUSINESS_TYPE_SEARCH_FIELDS = {
  name: { name: 'name' },
  description: { name: 'description' }
};

const EMPTY_COLUMN = { logic_operator: 'like', name: 'name', operator: 'AND', value: '' };

export const buildBusinessTypeSearchPayload = ({ start = 0, length = 1000, searchKey = 'all', searchValue = '' } = {}) => {
  const fb = new FilterBuilder().setPaging(start, length).setOrder(['id'], false);

  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      Object.values(BUSINESS_TYPE_SEARCH_FIELDS).forEach((f) => fb.addFilter(f.name, query));
    } else {
      const f = BUSINESS_TYPE_SEARCH_FIELDS[searchKey] || { name: searchKey };
      fb.like(f.name, query, 'AND');
    }
  }

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) {
    built.columns = [{ ...EMPTY_COLUMN }];
  }
  return built;
};

const extractList = (resp) => {
  const body = resp ?? {};
  const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body?.data?.data) ? body.data.data : Array.isArray(body) ? body : [];
  const total =
    body?.totalRecord ??
    body?.data?.totalRecord ??
    body?.totalRecords ??
    body?.data?.totalRecords ??
    body?.dataSize ??
    body?.data?.dataSize ??
    rows.length;
  return { rows, total: Number(total) || 0 };
};

export const fetchAllBusinessType = async ({ searchKey = 'all', searchValue = '', total = 0 } = {}) => {
  const payload = buildBusinessTypeSearchPayload({
    start: 0,
    length: total > 0 ? total : 9999,
    searchKey,
    searchValue
  });
  const resp = await fleetApi.post('/business-type/search' + getTzParam(), payload);
  return extractList(resp).rows;
};

export const useBusinessTypeList = (page = 0, rowsPerPage = 1000, searchKey = 'all', searchValue = '') => {
  const payload = useMemo(
    () => buildBusinessTypeSearchPayload({ start: page * rowsPerPage, length: rowsPerPage, searchKey, searchValue }),
    [page, rowsPerPage, searchKey, searchValue]
  );

  const key = useMemo(() => ['/business-type/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const { rows, total } = extractList(data);

  return {
    data: rows,
    totalRecord: total,
    isLoading,
    isError: error
  };
};

export const getBusinessTypeById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/business-type/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback search by ID (Sesuai pattern OSLOG V7 Anda)
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/business-type/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch business type by id:', err);
    throw err;
  }
};

export const createBusinessType = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.post(`/business-type/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateBusinessTypeList();
    return response.data;
  } catch (err) {
    console.error('Error creating business type:', err);
    throw err;
  }
};

export const updateBusinessType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };
    const response = await fleetApi.put(`/business-type/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateBusinessTypeList();
    mutate(`/business-type/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating business type:', err);
    throw err;
  }
};

export const deleteBusinessType = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/business-type/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateBusinessTypeList();
    return response.data;
  } catch (err) {
    console.error('Error deleting business type:', err);
    throw err;
  }
};
